// Dispute Workflow Actions
import { APPROVAL_ENGINE_ENDPOINTS } from "constants/approvalEngine";
import { instanceWithRetry } from "actions/axiosInstance";
import { handleAPIError } from "handlers/setters";

// Action Types
// Activate a dispute workflow
export const activateDisputeWorkflow =
  (workflowId) => async (dispatch, getState) => {
    // eslint-disable-next-line no-useless-catch
    try {
      // Backend ACTIVATE /v1.0/dispute/approvalengines/<id>/activate (POST, no payload)
      const response = await instanceWithRetry.post(
        `${APPROVAL_ENGINE_ENDPOINTS.ACTIVATE(workflowId)}?mutate=1`
      );
      if (response && response.status === 200) {
        const currentPage =
          getState()?.disputeWorkflow?.workflowSteps?.page || 1;
        dispatch(fetchDisputeWorkflowSteps(currentPage));
        return true;
      }
      return false;
    } catch (error) {
      throw error;
    }
  };

export const actionTypes = {
  FETCH_DISPUTE_WORKFLOW_STEPS: "FETCH_DISPUTE_WORKFLOW_STEPS",
  SET_DISPUTE_WORKFLOW_FETCHING: "SET_DISPUTE_WORKFLOW_FETCHING",
  SET_ALL_DISPUTE_WORKFLOWS: "SET_ALL_DISPUTE_WORKFLOWS",
  CREATE_DISPUTE_WORKFLOW: "CREATE_DISPUTE_WORKFLOW",
  UPDATE_DISPUTE_WORKFLOW: "UPDATE_DISPUTE_WORKFLOW",
  DELETE_DISPUTE_WORKFLOW: "DELETE_DISPUTE_WORKFLOW",
  ASSIGN_DISPUTE_WORKFLOW: "ASSIGN_DISPUTE_WORKFLOW",
};

/**
 * Fetches dispute workflow steps
 * @param {Object} params - Query parameters
 * @returns {Function} - Thunk action
 */
export const fetchDisputeWorkflowSteps =
  (page = 1) =>
  async (dispatch) => {
    dispatch({
      type: actionTypes.SET_DISPUTE_WORKFLOW_FETCHING,
      payload: true,
    });
    try {
      // Backend GET /approvalengines
      const response = await instanceWithRetry.get(
        `${APPROVAL_ENGINE_ENDPOINTS.LIST}?page=${page}`
      );
      let workflows =
        response && response.data && response.data.result
          ? response.data.result
          : [];
      // Map approval_levels_config to workflow_sequence_steps for UI compatibility
      workflows = workflows.map((wf) => ({
        ...wf,
        auto_approve_enabled: wf.auto_approve_enabled, // Ensure this field is always present
        workflow_sequence_steps: Array.isArray(wf.approval_levels_config)
          ? wf.approval_levels_config.map((step, idx) => ({
              ...step,
              step_seq: step.level_order || idx + 1,
              level_name: step.level_name,
              min_amount:
                wf.conditional_rules?.conditions?.find(
                  (c) =>
                    c.field === "amount" &&
                    (c.operator === ">" || c.operator === ">=")
                )?.value ?? 0,
              max_amount:
                wf.conditional_rules?.conditions?.find(
                  (c) =>
                    c.field === "amount" &&
                    (c.operator === "<" || c.operator === "<=")
                )?.value ?? null,
              dispute_type:
                wf.conditional_rules?.conditions?.find(
                  (c) => c.field === "reason_code"
                )?.value ?? "",
              is_archived: false,
            }))
          : [],
      }));
      dispatch({
        type: actionTypes.FETCH_DISPUTE_WORKFLOW_STEPS,
        payload: {
          data: workflows,
          page: Number(response.data.page) || page,
          pages: response.data.pages || 1,
          total: response.data.total || workflows.length,
          has_next: response.data.has_next || false,
          has_prev: response.data.has_prev || false,
        },
      });
      return workflows;
    } catch (error) {
      handleAPIError(error);
      dispatch({
        type: actionTypes.SET_DISPUTE_WORKFLOW_FETCHING,
        payload: false,
      });
      return [];
    }
  };

/**
 * Fetches ALL dispute workflows across all pages (for populating selectors/dropdowns).
 * Unlike fetchDisputeWorkflowSteps this thunk walks every page and returns the
 * full flat list.  It does NOT update pagination state in Redux.
 * @returns {Function} - Thunk action that resolves to the full workflow array
 */
export const fetchAllDisputeWorkflows = () => async (dispatch) => {
  const mapWorkflows = (raw) =>
    raw.map((wf) => ({
      ...wf,
      auto_approve_enabled: wf.auto_approve_enabled,
      workflow_sequence_steps: Array.isArray(wf.approval_levels_config)
        ? wf.approval_levels_config.map((step, idx) => ({
            ...step,
            step_seq: step.level_order || idx + 1,
            level_name: step.level_name,
            min_amount:
              wf.conditional_rules?.conditions?.find(
                (c) =>
                  c.field === "amount" &&
                  (c.operator === ">" || c.operator === ">=")
              )?.value ?? 0,
            max_amount:
              wf.conditional_rules?.conditions?.find(
                (c) =>
                  c.field === "amount" &&
                  (c.operator === "<" || c.operator === "<=")
              )?.value ?? null,
            dispute_type:
              wf.conditional_rules?.conditions?.find(
                (c) => c.field === "reason_code"
              )?.value ?? "",
            is_archived: false,
          }))
        : [],
    }));

  try {
    const allWorkflows = [];
    let page = 1;
    let hasNext = true;

    while (hasNext) {
      const response = await instanceWithRetry.get(
        `${APPROVAL_ENGINE_ENDPOINTS.LIST}?page=${page}`
      );
      const pageData = response?.data?.result ? response.data.result : [];
      allWorkflows.push(...mapWorkflows(pageData));
      hasNext = response?.data?.has_next || false;
      page += 1;
    }

    dispatch({
      type: actionTypes.SET_ALL_DISPUTE_WORKFLOWS,
      payload: allWorkflows,
    });
    return allWorkflows;
  } catch (error) {
    handleAPIError(error);
    return [];
  }
};

/**
 * Creates a new dispute workflow
 * @param {Object} workflowData - The workflow data to create
 * @returns {Function} - Thunk action
 */

export const createDisputeWorkflow = (workflowData) => async (dispatch) => {
  try {
    // Map workflowData to approval engine API format
    const requestData = {
      name: workflowData.name,
      description: workflowData.description || "workflow post call",
      approval_levels_config: workflowData.approval_levels_config || [],
      conditional_rules: workflowData.conditional_rules || {},
      auto_assign_to: workflowData.auto_assign_to,
      rule_priority:
        workflowData.rule_priority !== null &&
        workflowData.rule_priority !== undefined
          ? workflowData.rule_priority
          : null,
      customer_ids: workflowData.customer_ids || [],
      auto_approve_enabled: workflowData.auto_approve_enabled, // Ensure this is sent to backend
      reviewer_selection_strategy: workflowData.reviewer_selection_strategy,
    };

    const response = await instanceWithRetry.post(
      `${APPROVAL_ENGINE_ENDPOINTS.CREATE}?mutate=1`,
      requestData
    );

    if (response && response.data) {
      dispatch({
        type: actionTypes.CREATE_DISPUTE_WORKFLOW,
        payload: response.data.result,
      });
      return response.data.result;
    }
    return null;
  } catch (error) {
    handleAPIError(error);
    throw error;
  }
};

/**
 * Updates an existing dispute workflow
 * @param {string|number} workflowId - The workflow ID
 * @param {Object} workflowData - The updated workflow data
 * @returns {Function} - Thunk action
 */
export const updateDisputeWorkflow =
  (workflowId, workflowData) => async (dispatch) => {
    try {
      // PATCH /approvalengines/<engine_id> with same body as POST
      const response = await instanceWithRetry.patch(
        `${APPROVAL_ENGINE_ENDPOINTS.UPDATE(workflowId)}?mutate=1`,
        workflowData
      );
      if (response && response.data) {
        dispatch({
          type: actionTypes.UPDATE_DISPUTE_WORKFLOW,
          payload: { id: workflowId, data: response.data.result },
        });
        return response.data.result;
      }
      return null;
    } catch (error) {
      handleAPIError(error);
      throw error;
    }
  };

/**
 * Deletes a dispute workflow
 * @param {string|number} workflowId - The workflow ID to delete
 * @returns {Function} - Thunk action
 */
export const deleteDisputeWorkflow =
  (workflowId) => async (dispatch, getState) => {
    // eslint-disable-next-line no-useless-catch
    try {
      // Backend DEACTIVATE /v1.0/dispute/approvalengines/<id>/deactivate (POST, no payload)
      const response = await instanceWithRetry.post(
        `${APPROVAL_ENGINE_ENDPOINTS.DEACTIVATE(workflowId)}?mutate=1`
      );
      if (response && response.status === 200) {
        dispatch({
          type: actionTypes.DELETE_DISPUTE_WORKFLOW,
          payload: workflowId,
        });
        // Refresh the current page, not always page 1
        const currentPage =
          getState()?.disputeWorkflow?.workflowSteps?.page || 1;
        dispatch(fetchDisputeWorkflowSteps(currentPage));
        return true;
      }
      return false;
    } catch (error) {
      throw error;
    }

    /* Original API implementation - uncomment when backend is ready:
    return instanceWithRetry
      .delete(DISPUTE_WORKFLOW_ENDPOINTS.DELETE_WORKFLOW(workflowId))
      .then((response) => {
        if (response && response.status === 200) {
          dispatch({
            type: actionTypes.DELETE_DISPUTE_WORKFLOW,
            payload: workflowId,
          });
          // Refresh the workflow list
          dispatch(fetchDisputeWorkflowSteps());
          return true;
        }
        return false;
      })
      .catch((error) => {
        handleAPIError(error);
        throw error;
      });
    */
  };

/**
 * Assigns a workflow to disputes based on criteria
 * @param {Object} assignmentData - Assignment criteria and workflow ID
 * @returns {Function} - Thunk action
 */
export const assignDisputeWorkflow = (assignmentData) => async (dispatch) => {
  // eslint-disable-next-line no-useless-catch
  try {
    // Backend API call for workflow assignment
    const assignmentResult = {
      workflow_sequence_id: assignmentData.workflow_sequence_id,
      dispute_criteria: assignmentData.dispute_criteria,
      is_enabled: assignmentData.is_enabled || 1,
      override_workflow_assignment:
        assignmentData.override_workflow_assignment || 0,
      assigned_at: new Date().toISOString(),
    };

    dispatch({
      type: actionTypes.ASSIGN_DISPUTE_WORKFLOW,
      payload: assignmentResult,
    });

    return assignmentResult;
  } catch (error) {
    throw error;
  }

  /* Original API implementation - uncomment when backend is ready:
    const requestData = {
      workflow_sequence_id: assignmentData.workflow_sequence_id,
      dispute_criteria: assignmentData.dispute_criteria,
      is_enabled: assignmentData.is_enabled || 1,
      override_workflow_assignment:
        assignmentData.override_workflow_assignment || 0,
    };

    return instanceWithRetry
      .post(DISPUTE_WORKFLOW_ENDPOINTS.ASSIGN_WORKFLOW + '&mutate=1', requestData)
      .then((response) => {
        if (response && response.data) {
          dispatch({
            type: actionTypes.ASSIGN_DISPUTE_WORKFLOW,
            payload: response.data.result,
          });
          return response.data.result;
        }
        return null;
      })
      .catch((error) => {
        handleAPIError(error);
        throw error;
      });
    */
};

/**
 * Fetches the list of available reviewer strategies for the workflow dropdown.
 * @returns {Promise<Array<{value: string, label: string}>>}
 */
export const fetchReviewerStrategies = () => async () => {
  try {
    const response = await instanceWithRetry.get(
      "/v1.0/dispute/approvalengines/strategies"
    );
    return response?.data?.result || response?.data || [];
  } catch (error) {
    handleAPIError(error);
    return [];
  }
};

/**
 * Fetches reviewers for the default reviewers multiselect.
 * @param {Object} params - Search params
 * @param {string} params.q - Search text
 * @param {number} params.page - Page number
 * @param {number} params.per_page - Page size
 * @returns {Promise<Array<Object>>}
 */
export const fetchReviewerUsers = async ({
  q = "",
  page = 1,
  per_page = 10,
} = {}) => {
  try {
    const query = new URLSearchParams({
      q,
      capability: "REVIEWER",
      page,
      per_page,
    }).toString();
    const response = await instanceWithRetry.get(
      `/v1.0/dispute/usercapability/search?${query}`
    );
    const reviewers = response?.data?.result || response?.data || [];
    return reviewers.map(
      ({ id, user_id, username, email, capability_type }) => {
        const reviewerId = user_id ?? id;
        const reviewerLabel = username || email || `${reviewerId}`;
        return {
          label: reviewerLabel,
          value: reviewerId,
          id: reviewerId,
          user_id: reviewerId,
          username: reviewerLabel,
          email: email || "",
          capability_type,
        };
      }
    );
  } catch (error) {
    handleAPIError(error);
    return [];
  }
};

/**
 * Gets a specific dispute workflow by ID
 * @param {string|number} workflowId - The workflow ID
 * @returns {Function} - Thunk action
 */
export const getDisputeWorkflowById = (workflowId) => async () => {
  try {
    // Backend implementation
    const response = await instanceWithRetry.get(
      APPROVAL_ENGINE_ENDPOINTS.GET_BY_ID(workflowId)
    );
    if (
      response &&
      response.data &&
      response.data.result &&
      response.data.result.length > 0
    ) {
      return response.data.result[0];
    }
    return null;
  } catch (error) {
    handleAPIError(error);
    return null;
  }
};
